import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["map", "filterCountry", "filterCity", "searchInput", "clearFilters", "activeFilters"]
  static values = {
    venues: Array,
    centerLat: Number,
    centerLng: Number,
    zoom: Number
  }

  connect() {
    if (typeof mapboxgl === 'undefined') {
      return
    }

    // Initialize state
    this.currentFilters = {
      country: 'all',
      city: 'all',
      radius: null
    }

    this.hoveredMarkerId = null
    this.hoveredRowId = null

    // Initialize map
    this.initializeMap()
  }

  disconnect() {

    // Clean up map
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  initializeMap() {

    const token = this.authToken
    if (!token) {
      this.showMapError("Token Mapbox manquant")
      return
    }

    mapboxgl.accessToken = token

    try {
      // Wait for the map container to be properly sized
      setTimeout(() => {
        this.map = new mapboxgl.Map({
          container: this.mapTarget,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [this.centerLngValue || 2.3522, this.centerLatValue || 48.8566],
          zoom: this.zoomValue || 5,
          projection: 'mercator', // Use flat Mercator projection instead of globe
          pitch: 0, // Disable tilt
          bearing: 0 // Reset rotation
        })

        this.map.addControl(new mapboxgl.NavigationControl())

        this.map.on('load', () => {
          this.addVenueMarkersWithClustering()
          this.setupMapInteractions()
        })

        this.map.on('error', (e) => {
          this.showMapError("Erreur lors du chargement de la carte")
        })

        // Handle map resize when sidebar toggles
        this.map.on('idle', () => {
          this.map.resize()
        })
      }, 100)
    } catch (error) {
      this.showMapError("Impossible d'initialiser la carte")
    }
  }


  highlightMapMarker(venueId) {
    if (!this.map || !this.map.getSource('venues')) return

    // Find the venue data
    const venue = this.venuesValue.find(v => v.id == venueId)
    if (!venue || !venue.latitude || !venue.longitude) {
      return
    }


    // Remove any previous highlight
    if (this.hoveredMarkerId && this.hoveredMarkerId !== venueId) {
      this.map.setFeatureState(
        { source: 'venues', id: parseInt(this.hoveredMarkerId) },
        { hover: false }
      )
    }

    // Set feature state for highlight
    this.map.setFeatureState(
      { source: 'venues', id: parseInt(venueId) },
      { hover: true }
    )

    // Store current hovered marker
    this.hoveredMarkerId = venueId
  }

  unhighlightMapMarker(venueId) {
    if (!this.map || !this.map.getSource('venues')) return


    // Remove feature state
    this.map.setFeatureState(
      { source: 'venues', id: parseInt(venueId) },
      { hover: false }
    )

    // Clear if this was the hovered marker
    if (this.hoveredMarkerId == venueId) {
      this.hoveredMarkerId = null
    }
  }

  centerMapOnVenue(venueId) {
    const venue = this.venuesValue.find(v => v.id == venueId)
    if (!venue || !venue.latitude || !venue.longitude) return

    this.map.flyTo({
      center: [venue.longitude, venue.latitude],
      zoom: 14,
      duration: 1000
    })
  }

  setupMapInteractions() {

    // Hover effect on clusters/markers
    this.map.on('mouseenter', 'unclustered-point', (e) => {
      this.map.getCanvas().style.cursor = 'pointer'

      const properties = e.features[0].properties
      const venueId = properties.id


      // Highlight the corresponding table row
      this.highlightTableRow(venueId)
    })

    this.map.on('mouseleave', 'unclustered-point', () => {
      this.map.getCanvas().style.cursor = ''


      // Remove highlight from table row
      this.unhighlightTableRow()
    })

    // Click on marker
    this.map.on('click', 'unclustered-point', (e) => {
      const properties = e.features[0].properties

      // Parse properties (they come as strings from GeoJSON)
      const venue = {
        id: properties.id,
        name: properties.name,
        city: properties.city,
        country: properties.country,
        email: properties.email,
        edit_url: properties.edit_url,
        latitude: e.features[0].geometry.coordinates[1],
        longitude: e.features[0].geometry.coordinates[0]
      }

      this.showVenuePopup(venue)
    })

    // Click on cluster
    this.map.on('click', 'clusters', (e) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['clusters']
      })

      const clusterId = features[0].properties.cluster_id
      this.map.getSource('venues').getClusterExpansionZoom(
        clusterId,
        (err, zoom) => {
          if (err) return

          this.map.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom
          })
        }
      )
    })
  }

  highlightTableRow(venueId) {
    // Removed sidebar highlighting - no longer needed
  }

  unhighlightTableRow() {
    // Removed sidebar highlighting - no longer needed
  }

  addVenueMarkersWithClustering() {
    // Filter venues with coordinates
    const venuesWithCoords = this.venuesValue.filter(v => v.latitude && v.longitude)

    // Apply jitter to prevent stacked markers at same location
    const venuesWithJitter = this.applyJitterToOverlappingVenues(venuesWithCoords)

    // Convert to GeoJSON - IMPORTANT: add id at the root level for setFeatureState to work
    const geojson = {
      type: 'FeatureCollection',
      features: venuesWithJitter.map((venue, index) => ({
        type: 'Feature',
        id: parseInt(venue.id), // IMPORTANT: id must be at the root level of the feature
        geometry: {
          type: 'Point',
          coordinates: [venue.longitude, venue.latitude]
        },
        properties: {
          id: venue.id,
          name: venue.name,
          city: venue.city,
          country: venue.country,
          email: venue.email,
          edit_url: venue.edit_url
        }
      }))
    }

    // Add source
    this.map.addSource('venues', {
      type: 'geojson',
      data: geojson,
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50
    })

    // Add layers
    this.map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'venues',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#51bbd6',
          15,
          '#f1f075',
          30,
          '#f28cb1'
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          20,
          15,
          30,
          30,
          40
        ]
      }
    })

    this.map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'venues',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
      }
    })

    this.map.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'venues',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          '#f59e0b',  // Orange when hovered
          '#11b4da'   // Default blue
        ],
        'circle-radius': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          10,  // Larger when hovered
          7    // Default size
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff'
      }
    })

    // Fit map to venue bounds
    if (venuesWithCoords.length > 0) {
      const bounds = new mapboxgl.LngLatBounds()
      venuesWithCoords.forEach(venue => {
        bounds.extend([venue.longitude, venue.latitude])
      })
      this.map.fitBounds(bounds, { padding: 50 })
    }
  }

  // Filter methods
  filterVenues() {
    const searchTerm = this.hasSearchInputTarget ? this.searchInputTarget.value.toLowerCase() : ''
    const country = this.hasFilterCountryTarget ? this.filterCountryTarget.value : 'all'
    const city = this.hasFilterCityTarget ? this.filterCityTarget.value : 'all'
    
    
    // Filter venues
    const filteredVenues = this.venuesValue.filter(venue => {
      // Search filter
      if (searchTerm && !venue.name.toLowerCase().includes(searchTerm) && 
          !venue.city?.toLowerCase().includes(searchTerm) &&
          !venue.country?.toLowerCase().includes(searchTerm)) {
        return false
      }
      
      // Country filter
      if (country !== 'all' && venue.country !== country) {
        return false
      }
      
      // City filter
      if (city !== 'all' && venue.city !== city) {
        return false
      }
      
      return true
    })
    
    // Update map markers
    this.updateMapMarkers(filteredVenues)
    
    // Update sidebar
    this.updateSidebar(filteredVenues)
    
    // Show/hide clear button
    if (searchTerm || country !== 'all' || city !== 'all') {
      this.clearFiltersTarget?.classList.remove('hidden')
    } else {
      this.clearFiltersTarget?.classList.add('hidden')
    }
  }
  
  clearFilters() {
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ''
    if (this.hasFilterCountryTarget) this.filterCountryTarget.value = 'all'
    if (this.hasFilterCityTarget) this.filterCityTarget.value = 'all'
    
    this.filterVenues()
  }
  
  updateMapMarkers(venues) {
    if (!this.map || !this.map.getSource('venues')) return

    const venuesWithCoords = venues.filter(v => v.latitude && v.longitude)

    // Apply jitter to prevent stacked markers (same as initial load)
    const venuesWithJitter = this.applyJitterToOverlappingVenues(venuesWithCoords)

    const geojson = {
      type: 'FeatureCollection',
      features: venuesWithJitter.map(venue => ({
        type: 'Feature',
        id: parseInt(venue.id),
        geometry: {
          type: 'Point',
          coordinates: [venue.longitude, venue.latitude]
        },
        properties: {
          id: venue.id,
          name: venue.name,
          city: venue.city,
          country: venue.country,
          email: venue.email,
          edit_url: venue.edit_url
        }
      }))
    }

    this.map.getSource('venues').setData(geojson)
  }
  
  updateSidebar(venues) {
    // Sidebar removed - no longer needed
  }

  showVenuePopup(venue) {
    const popupContent = `
      <div class="min-w-[280px]">
        <!-- Header with venue name -->
        <div class="px-4 pt-4 pb-3 border-b border-gray-100">
          <h3 class="text-lg font-semibold text-gray-900 mb-1">${venue.name}</h3>
          <div class="flex items-center gap-1.5 text-sm text-gray-600">
            <svg class="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
            </svg>
            <span>${venue.city}, ${venue.country}</span>
          </div>
        </div>

        <!-- Info section -->
        ${venue.email ? `
          <div class="px-4 py-3 border-b border-gray-100">
            <div class="flex items-center gap-2 text-sm text-gray-600">
              <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              <span class="truncate">${venue.email}</span>
            </div>
          </div>
        ` : ''}

        <!-- Actions -->
        <div class="px-4 py-3 bg-gray-50">
          <a href="${venue.edit_url}"
             data-turbo-frame="modal"
             class="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors shadow-sm">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
            </svg>
            <span>Modifier le lieu</span>
          </a>
        </div>
      </div>
    `

    // Create popup with custom styling
    const popup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: true,
      maxWidth: '320px',
      className: 'venue-popup'
    })
      .setLngLat([venue.longitude, venue.latitude])
      .setHTML(popupContent)
      .addTo(this.map)

    return popup
  }

  showMapError(message) {
    if (this.mapTarget) {
      this.mapTarget.innerHTML = `
        <div class="flex items-center justify-center h-full bg-gray-100 text-gray-600">
          <div class="text-center">
            <i class="fas fa-exclamation-triangle text-4xl mb-4 text-orange-500"></i>
            <p class="text-lg font-medium mb-2">Erreur de carte</p>
            <p class="text-sm">${message}</p>
          </div>
        </div>
      `
    }
  }

  get authToken() {
    // Get token from meta tag (same as other map controllers)
    const meta = document.head.querySelector('meta[name="mapbox_token"]')
    if (!meta) {
      return null
    }
    return meta.content
  }

  // Apply small random offset to venues at exact same coordinates
  applyJitterToOverlappingVenues(venues) {
    // Group venues by exact coordinates (rounded to avoid floating point issues)
    const coordsMap = new Map()

    venues.forEach(venue => {
      const key = `${venue.latitude.toFixed(4)},${venue.longitude.toFixed(4)}`
      if (!coordsMap.has(key)) {
        coordsMap.set(key, [])
      }
      coordsMap.get(key).push(venue)
    })

    // Apply jitter to venues at same location
    const jitteredVenues = []
    let overlappingCount = 0

    coordsMap.forEach((venuesAtLocation, coordsKey) => {
      if (venuesAtLocation.length === 1) {
        // Single venue at this location - no jitter needed
        jitteredVenues.push(venuesAtLocation[0])
      } else {
        // Multiple venues at same location - apply jitter
        overlappingCount += venuesAtLocation.length

        venuesAtLocation.forEach((venue, index) => {
          const jitteredVenue = { ...venue }

          // Apply larger offset for better visibility
          // 0.001 degrees ≈ 111 meters at equator
          const jitterAmount = 0.002 // ~220 meters for better separation
          const angle = (2 * Math.PI * index) / venuesAtLocation.length // Distribute in circle
          const radius = jitterAmount * (0.7 + Math.random() * 0.3) // Random radius variation

          jitteredVenue.latitude = venue.latitude + (radius * Math.sin(angle))
          jitteredVenue.longitude = venue.longitude + (radius * Math.cos(angle))

          jitteredVenues.push(jitteredVenue)
        })
      }
    })

    return jitteredVenues
  }

}
